import { atom, useRecoilState } from "recoil";
import useFeedbackConfig from "./useFeedbackConfig";
import { QUESTION_TYPES } from "../constants/feedback.constants";

const INITIAL_QUESTIONS_STATE = [];

const feedbackQuestionsStateAtom = atom({
	key: "feedbackQuestionsState",
	default: INITIAL_QUESTIONS_STATE,
});

const getAnswerStructure = (inputType, answer) => {
	if (answer) {
		if (inputType === QUESTION_TYPES.RATING) {
			const { value, name, decisionConditional } = answer;
			return [{ value, name, decisionConditional }];
		}
		if (inputType === QUESTION_TYPES.SINGLE_SELECT) {
			const { code, label, decisionConditional } = answer;
			return [{ code, label, decisionConditional }];
		}
		if (inputType === QUESTION_TYPES.MULTI_SELECT) {
			return answer.map(({ code, label, decisionConditional }) => ({ code, label, decisionConditional }));
		}
		return answer;
	}
	if (inputType === QUESTION_TYPES.TEXT_INPUT || inputType === QUESTION_TYPES.TEXT_AREA) {
		return "";
	}
	return [];
};

const useFeedbackQuestionsState = () => {
	const {
		feedbackConfig
	} = useFeedbackConfig();
	const { title = '' } = feedbackConfig || {}
	const [questions, setQuestions] = useRecoilState(feedbackQuestionsStateAtom);

	const getQuestionnaireFeedback = (allQuestions, currentQuestionIndex) => {
		const { inputType, answer } = allQuestions[currentQuestionIndex];
		let newIndex = currentQuestionIndex + 1;
		let feedback = getAnswerStructure(inputType, answer);

		const getDecisionalConditionalFeedback = (questionIndex) => {
			const { statement, inputType, answer } = allQuestions[questionIndex];
			let feedback = getAnswerStructure(inputType, answer);
			newIndex = Math.max(newIndex, questionIndex + 1);
			if (Array.isArray(feedback)) {
				feedback = feedback.map((feedbackEntry) => {
					const { decisionConditional } = feedbackEntry;
					let res = { ...feedbackEntry };
					if (decisionConditional) {
						const decisionQuestionIndex = allQuestions.findIndex(
							({ decisionConditionalId }) => decisionConditional === decisionConditionalId
						);
						res = { ...res, ...getDecisionalConditionalFeedback(decisionQuestionIndex) };
						delete res.decisionConditional;
					}
					return res;
				});
			}
			return {
				statement,
				feedback,
			};
		};

		if (Array.isArray(feedback)) {
			feedback = feedback.map((fb, index) => {
				const { decisionConditional } = fb;
				let res = { ...fb };
				if (decisionConditional) {
					res = { ...res, ...getDecisionalConditionalFeedback(currentQuestionIndex + index + 1) };
					delete res.decisionConditional;
				}
				return res;
			});
		}

		return { feedback, newIndex };
	};

	const getPayloadFromQuestions = (aditionalQuestions = [], metaId = '') => {
		const query = new URLSearchParams(location.search);
		const uniqueId = query.get("uniqueId") || metaId;
		const allQuestions = [...questions, ...aditionalQuestions];

		const payload = {
			title: title,
			questionnaire: [],
		};
		let currentQuestionIndex = 0;
		while (currentQuestionIndex < allQuestions.length) {
			const { order, type, statement } = allQuestions[currentQuestionIndex];
			if (order) {
				const { feedback, newIndex } = getQuestionnaireFeedback(allQuestions, currentQuestionIndex);
				payload.questionnaire[order - 1] = {
					type,
					statement,
					feedback,
				};
				currentQuestionIndex = newIndex;
			}
		}
		return { id: uniqueId, data: payload };
	};

	const addQuestions = (newQuestions) => {
		setQuestions((prevQuestions) => [...prevQuestions, ...newQuestions]);
	};

	const removeQuestion = () => {
		return new Promise(resolve => {
			setQuestions((curQuestions) => {
				resolve(curQuestions[curQuestions.length - 1]);
				return curQuestions.slice(0, curQuestions.length - 1);
			})
		})
	};

	return { questions, addQuestions, removeQuestion, getPayloadFromQuestions, setQuestions };
};

export default useFeedbackQuestionsState;
